import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add as AddIcon, History as HistoryIcon } from "@mui/icons-material";
import api from "../../services/api";
import EmploymentHistoryList, {
  EmploymentRecord,
} from "../shared/EmploymentHistoryList";

interface Employee {
  id: number;
  name: string;
  employee_code: string;
}

interface Designation {
  id: number;
  name: string;
}

interface Grade {
  id: number;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

const EmploymentHistoryManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [employmentHistory, setEmploymentHistory] = useState<
    EmploymentRecord[]
  >([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    employee_id: 0,
    designation_id: 0,
    grade_id: 0,
    department_id: 0,
    start_date: "",
    end_date: "",
    appointment_type: "new_appointment",
    remarks: "",
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      fetchEmploymentHistory(selectedEmployee);
    }
  }, [selectedEmployee]);

  const fetchInitialData = async () => {
    try {
      const [empRes, desRes, grdRes, deptRes] = await Promise.all([
        api.get("/admin/employees"),
        api.get("/designations"),
        api.get("/grades"),
        api.get("/admin/departments"),
      ]);
      setEmployees(empRes.data);
      setDesignations(desRes.data);
      setGrades(grdRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setError("Failed to load initial data");
    }
  };

  const fetchEmploymentHistory = async (employeeId: number) => {
    setLoading(true);
    try {
      const response = await api.get(
        `/employment-history/employee/${employeeId}`,
      );
      setEmploymentHistory(response.data);
    } catch (error) {
      console.error("Error fetching employment history:", error);
      setError("Failed to fetch employment history");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: EmploymentRecord) => {
    setEditingRecord(record.id);
    setFormData({
      employee_id: record.employee_id,
      designation_id: parseInt(
        designations
          .find((d) => d.name === record.designation_name)
          ?.id.toString() || "0",
      ),
      grade_id: parseInt(
        grades.find((g) => g.name === record.grade_name)?.id.toString() || "0",
      ),
      department_id: parseInt(
        departments
          .find((d) => d.name === record.department_name)
          ?.id.toString() || "0",
      ),
      start_date: record.start_date.split("T")[0],
      end_date: record.end_date ? record.end_date.split("T")[0] : "",
      appointment_type: record.appointment_type,
      remarks: record.remarks || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRecord) {
        await api.put(`/employment-history/${editingRecord}`, formData);
      } else {
        await api.post("/employment-history", formData);
      }
      setShowModal(false);
      setEditingRecord(null);
      setFormData({
        employee_id: selectedEmployee || 0,
        designation_id: 0,
        grade_id: 0,
        department_id: 0,
        start_date: "",
        end_date: "",
        appointment_type: "new_appointment",
        remarks: "",
      });
      if (selectedEmployee) {
        fetchEmploymentHistory(selectedEmployee);
      }
    } catch (error: any) {
      setError(
        error.response?.data?.error ||
          `Failed to ${editingRecord ? "update" : "add"} employment record`,
      );
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        Employment History Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Employee Selection */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ height: "70vh", display: "flex", flexDirection: "column" }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e0e0e0",
                bgcolor: "#fafafa",
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Employees
              </Typography>
            </Box>
            <List sx={{ overflowY: "auto", flexGrow: 1 }}>
              {employees.map((employee) => (
                <ListItem key={employee.id} disablePadding>
                  <ListItemButton
                    selected={selectedEmployee === employee.id}
                    onClick={() => setSelectedEmployee(employee.id)}
                  >
                    <ListItemText
                      primary={employee.name}
                      secondary={employee.employee_code}
                      primaryTypographyProps={{
                        fontWeight:
                          selectedEmployee === employee.id ? "bold" : "normal",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Employment History Display */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ p: 3, height: "70vh", overflowY: "auto" }}
          >
            {selectedEmployee ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <HistoryIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      History for{" "}
                      {employees.find((e) => e.id === selectedEmployee)?.name}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingRecord(null);
                      setFormData({
                        employee_id: selectedEmployee,
                        designation_id: 0,
                        grade_id: 0,
                        department_id: 0,
                        start_date: "",
                        end_date: "",
                        appointment_type: "new_appointment",
                        remarks: "",
                      });
                      setShowModal(true);
                    }}
                  >
                    Add Record
                  </Button>
                </Box>

                {loading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 8 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <EmploymentHistoryList
                    history={employmentHistory}
                    onEdit={handleEdit}
                    editable={true}
                  />
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <HistoryIcon sx={{ fontSize: 64, mb: 2, opacity: 0.2 }} />
                <Typography variant="body1">
                  Select an employee to view their employment history
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Employment Record Dialog */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRecord(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingRecord ? "Edit Employment Record" : "Add Employment Record"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Designation"
                  value={formData.designation_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      designation_id: parseInt(e.target.value),
                    })
                  }
                  required
                >
                  <MenuItem value={0} disabled>
                    Select Designation
                  </MenuItem>
                  {designations.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Grade"
                  value={formData.grade_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      grade_id: parseInt(e.target.value),
                    })
                  }
                >
                  <MenuItem value={0}>Select Grade (Optional)</MenuItem>
                  {grades.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Department"
                  value={formData.department_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      department_id: parseInt(e.target.value),
                    })
                  }
                  required
                >
                  <MenuItem value={0} disabled>
                    Select Department
                  </MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Appointment Type"
                  value={formData.appointment_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      appointment_type: e.target.value,
                    })
                  }
                  required
                >
                  <MenuItem value="new_appointment">New Appointment</MenuItem>
                  <MenuItem value="promotion">Promotion</MenuItem>
                  <MenuItem value="transfer">Transfer</MenuItem>
                  <MenuItem value="grade_change">Grade Change</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  fullWidth
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  fullWidth
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Leave blank for current"
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                  helperText="Leave empty for current position"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) =>
                    setFormData({ ...formData, remarks: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setShowModal(false);
                setEditingRecord(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingRecord ? "Update" : "Add"} Record
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default EmploymentHistoryManagement;
